/**
 * Tech Request Hub - Firestore data access.
 *
 * Every write goes through the same shapes firestore.rules validates. Updates
 * carry prevUpdatedAt (the updatedAt we last saw) so two people editing the
 * same request can't silently overwrite each other - a stale write is
 * rejected by the rules and surfaced as a conflict.
 */
import { useEffect, useState } from 'react';
import {
  addDoc, collection, deleteDoc, doc, getDoc, getDocs, limit, onSnapshot, orderBy, query, serverTimestamp,
  Timestamp, updateDoc, where, writeBatch, type DocumentData,
} from 'firebase/firestore';
import type { User } from 'firebase/auth';
import { auth, db } from '../firebase';
import {
  DEFAULT_SETTINGS, OWNER_UID, type DivisionId, type PlannerSlot, type RequestType, type Settings,
  type Source, type Status, type UrgencyHint,
} from '../config';

export interface Link { label: string; url: string }

export interface Req {
  id: string;
  title: string;
  description?: string;
  type: RequestType;
  divisions: DivisionId[];
  status: Status;
  requesterName: string;
  requesterEmail: string;
  requesterUid?: string;
  source: Source;
  addedBy?: string;
  owner?: string;
  impact?: number;
  urgency?: number;
  rank?: number;
  rankManual?: boolean;
  estHours?: number;
  loggedHours?: number;
  plannerSlot?: PlannerSlot;
  plannerDate?: string;
  urgencyHint?: UrgencyHint;
  waitingOn?: string;
  links?: Link[];
  releaseNote?: string;
  stageTimes?: Record<string, Timestamp>;
  completedAt?: Timestamp;
  importKey?: string;
  /** "Also notify": extra @trustroofing.com addresses CC'd on the Got it / Done emails. */
  notify?: string[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface Activity {
  id: string;
  text: string;
  kind: 'note' | 'status' | 'system';
  author: string;
  authorUid: string;
  createdAt: Timestamp | null;
}

export type Role = 'editor' | 'staff';

export interface Me {
  uid: string;
  name: string;
  email: string;
  role: Role;
  division?: DivisionId;
}

const toReq = (id: string, d: DocumentData) => ({ id, ...d }) as Req;

/** Resolve who the signed-in user is: editor if they can read config/roles. */
export function useMe(user: User | null): { me: Me | null; loading: boolean } {
  const [state, setState] = useState<{ me: Me | null; loading: boolean }>({ me: null, loading: true });
  useEffect(() => {
    if (!user) { setState({ me: null, loading: false }); return; }
    let live = true;
    (async () => {
      // Make sure this person shows up in Admin (people/{uid}); best-effort.
      try {
        const { httpsCallable } = await import('firebase/functions');
        const { functions } = await import('../firebase');
        await httpsCallable(functions, 'registerSelf')({});
      } catch { /* not a Trust Roofing Microsoft account, or offline */ }
      // The owner is always an editor, even if the roles doc is missing or unreadable.
      let role: Role = user.uid === OWNER_UID ? 'editor' : 'staff';
      try { await getDoc(doc(db, 'config/roles')); role = 'editor'; } catch { /* staff */ }
      let division: DivisionId | undefined;
      let name = user.displayName || user.email || 'Unknown';
      try {
        const p = await getDoc(doc(db, 'people', user.uid));
        if (p.exists()) {
          const d = p.get('division');
          division = d && d !== 'all' ? d : undefined; // 'all' = not assigned yet
          name = p.get('name') || name;
        }
      } catch { /* no directory entry yet */ }
      if (live) setState({ me: { uid: user.uid, name, email: user.email || '', role, division }, loading: false });
    })();
    return () => { live = false; };
  }, [user]);
  return state;
}

/** Editors: every request, live. Staff: only their own (scoped query - rules deny unscoped). */
export function useRequests(me: Me | null) {
  const [items, setItems] = useState<Req[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!me) { setItems([]); return; }
    const base = collection(db, 'requests');
    const q = me.role === 'editor'
      ? query(base, orderBy('createdAt', 'desc'))
      : query(base, where('requesterUid', '==', me.uid));
    return onSnapshot(q,
      (snap) => { setItems(snap.docs.map((d) => toReq(d.id, d.data()))); setError(null); },
      (e) => setError(e.message));
  }, [me?.uid, me?.role]);
  return { items, error };
}

export function useActivity(requestId: string | null) {
  const [items, setItems] = useState<Activity[]>([]);
  useEffect(() => {
    if (!requestId) return;
    const q = query(collection(db, 'requests', requestId, 'activity'), orderBy('createdAt', 'asc'));
    return onSnapshot(q, (snap) => setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Activity)), () => setItems([]));
  }, [requestId]);
  return items;
}

export function useSettings(me: Me | null): Settings {
  const [s, setS] = useState<Settings>(DEFAULT_SETTINGS);
  useEffect(() => {
    if (me?.role !== 'editor') return;
    return onSnapshot(doc(db, 'config/settings'), (snap) => {
      if (snap.exists()) setS({ ...DEFAULT_SETTINGS, ...(snap.data() as Partial<Settings>) });
    }, () => undefined);
  }, [me?.role]);
  return s;
}

// ---- writes ---------------------------------------------------------------

function clean<T extends Record<string, unknown>>(o: T): T {
  // Firestore rejects undefined; rules reject unknown keys - drop empties.
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== '')) as T;
}

/** Staff intake form. Shape is exactly what the rules allow a submitter to write. */
export async function submitRequest(me: Me, f: {
  title: string; description: string; type: RequestType; divisions: DivisionId[];
  urgencyHint: UrgencyHint; link?: string;
}) {
  return addDoc(collection(db, 'requests'), clean({
    title: f.title.trim(),
    description: f.description.trim(),
    type: f.type,
    divisions: f.divisions,
    status: 'new',
    // Must match the verified token (rules): display name, else email.
    requesterName: auth.currentUser?.displayName || auth.currentUser?.email || me.email,
    requesterEmail: me.email,
    requesterUid: me.uid,
    source: 'form',
    urgencyHint: f.urgencyHint,
    links: safeUrl(f.link) ? [{ label: 'Attachment / link', url: f.link!.trim() }] : undefined,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }));
}

/** Editor quick add (and anything else an editor creates). */
export async function createRequest(me: Me, f: {
  title: string; requesterName: string; requesterEmail?: string; divisions: DivisionId[];
  type?: RequestType; source?: Source; description?: string;
}) {
  const ref = await addDoc(collection(db, 'requests'), clean({
    title: f.title.trim(),
    description: f.description?.trim(),
    type: f.type ?? 'support',
    divisions: f.divisions.length ? f.divisions : ['all'],
    status: 'new',
    requesterName: f.requesterName.trim() || me.name,
    requesterEmail: f.requesterEmail?.trim() || '',
    source: f.source ?? 'quick',
    addedBy: me.name,
    owner: 'Baker',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }));
  await addActivity(me, ref.id, `Added by ${me.name} (${f.source ?? 'quick add'})`, 'system');
  return ref;
}

export class ConflictError extends Error {
  constructor() { super('Someone else changed this request a moment ago. Your view has refreshed - try again.'); }
}

/**
 * Update a request. Handles the concurrency echo, stage timestamps and the
 * status-change activity entry in one place so every view behaves the same.
 */
export async function updateRequest(me: Me, r: Req, patch: Partial<Omit<Req, 'id' | 'createdAt' | 'updatedAt'>>) {
  const data: Record<string, unknown> = { ...patch, updatedAt: serverTimestamp(), prevUpdatedAt: r.updatedAt };
  const statusChanged = patch.status && patch.status !== r.status;
  if (statusChanged) {
    data[`stageTimes.${patch.status}`] = serverTimestamp();
    if (patch.status === 'completed') data.completedAt = serverTimestamp();
  }
  for (const [k, v] of Object.entries(data)) if (v === undefined) delete data[k];
  try {
    await updateDoc(doc(db, 'requests', r.id), data);
  } catch (e) {
    if ((e as { code?: string }).code === 'permission-denied') throw new ConflictError();
    throw e;
  }
  if (statusChanged) await addActivity(me, r.id, `Status: ${r.status} -> ${patch.status}`, 'status');
}

/** Remove optional fields (rules reject undefined, so this uses deleteField). */
export async function clearFields(me: Me, r: Req, fields: (keyof Req)[]) {
  const { deleteField } = await import('firebase/firestore');
  const data: Record<string, unknown> = { updatedAt: serverTimestamp(), prevUpdatedAt: r.updatedAt };
  for (const f of fields) data[f as string] = deleteField();
  await updateDoc(doc(db, 'requests', r.id), data);
  void me;
}

export async function addActivity(me: Me, requestId: string, text: string, kind: Activity['kind'] = 'note') {
  return addDoc(collection(db, 'requests', requestId, 'activity'), {
    text: text.slice(0, 5000), kind, author: me.name, authorUid: me.uid, createdAt: serverTimestamp(),
  });
}

/** Lineup: write several ranks at once (one batch, each with its own echo). */
export async function writeRanks(rs: { r: Req; rank: number; manual: boolean }[]) {
  // Firestore caps a batch at 500 writes; chunk (each chunk is atomic on its own).
  for (let i = 0; i < rs.length; i += 400) {
    const b = writeBatch(db);
    for (const { r, rank, manual } of rs.slice(i, i + 400)) {
      b.update(doc(db, 'requests', r.id), { rank, rankManual: manual, updatedAt: serverTimestamp(), prevUpdatedAt: r.updatedAt });
    }
    await b.commit();
  }
}

// ---- helpers --------------------------------------------------------------

export function ageDays(t?: Timestamp | null): number {
  if (!t) return 0;
  return Math.max(0, Math.floor((Date.now() - t.toMillis()) / 86400000));
}
export const ageLabel = (t?: Timestamp | null) => { const d = ageDays(t); return d === 0 ? 'today' : `${d}d`; };

/** Days in the current stage (from stageTimes, falling back to createdAt). */
export function daysInStage(r: Req): number {
  return ageDays(r.stageTimes?.[r.status] ?? r.createdAt);
}

export const shortId = (id: string) => id.slice(0, 5).toUpperCase();

/** "Also notify" addresses: Trust Roofing only, stored lowercase (rules enforce the same). */
export const NOTIFY_MAX = 10;
export const notifyAddr = (e?: string | null) => {
  const v = (e ?? '').trim().toLowerCase();
  return /^[a-z0-9._%+-]+@trustroofing\.com$/.test(v) ? v : null;
};

/** Only http(s) links are ever stored or rendered as clickable (no javascript:/data:). */
export const safeUrl = (u?: string | null) => !!u && /^https?:\/\/\S+$/i.test(u.trim());

// ---- planner --------------------------------------------------------------

/** Drop a request into a planner slot (stamped with the date it's planned for). */
export async function setPlanner(me: Me, r: Req, slot: PlannerSlot) {
  const { plannedDateFor } = await import('./order');
  // plannerDate is left as-is for 'none' - effectiveSlot ignores it there.
  await updateRequest(me, r, slot === 'none' ? { plannerSlot: 'none' } : { plannerSlot: slot, plannerDate: plannedDateFor(slot) });
}

/** Log hours on a planner item. keep=true leaves it on Today; false takes it off the plan. */
export async function logWork(me: Me, r: Req, hours: number, keep: boolean) {
  await logSession(me, r, hours, keep ? undefined : { extra: { plannerSlot: 'none' } });
  await addActivity(me, r.id, `Worked ${hours}h${keep ? ' (still on today)' : ' - off the plan'}`, 'system');
}

// ---- time sessions -------------------------------------------------------------
//
// Each logged chunk of work is a doc in requests/{id}/sessions. The request's
// loggedHours stays the total everything else reads (Completed, charts): every
// session add/edit/delete moves it by the same delta in ONE batch, so the two
// never drift. Editing the total by hand is allowed; the difference from the
// session sum shows as a "manual adjustment".

export interface Session {
  id: string;
  hours: number;
  note?: string;
  date: Timestamp;
  by: string;
  byUid: string;
  createdAt: Timestamp | null;
}

export const SESSION_MAX_HOURS = 24;
export const roundH = (n: number) => Math.round(n * 100) / 100;

export function useSessions(requestId: string | null) {
  const [items, setItems] = useState<Session[]>([]);
  useEffect(() => {
    if (!requestId) return;
    const q = query(collection(db, 'requests', requestId, 'sessions'), orderBy('date', 'desc'));
    return onSnapshot(q, (snap) => setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Session)), () => setItems([]));
  }, [requestId]);
  return items;
}

/** Write a batch that also moves the request's total; a stale request reads as a conflict. */
async function commitWithTotal(r: Req, delta: number, write: (b: ReturnType<typeof writeBatch>) => void, extra: Partial<Req> = {}) {
  const b = writeBatch(db);
  write(b);
  b.update(doc(db, 'requests', r.id), {
    ...extra, loggedHours: Math.max(0, roundH((r.loggedHours ?? 0) + delta)),
    updatedAt: serverTimestamp(), prevUpdatedAt: r.updatedAt,
  });
  try {
    await b.commit();
  } catch (e) {
    if ((e as { code?: string }).code === 'permission-denied') throw new ConflictError();
    throw e;
  }
}

export async function logSession(me: Me, r: Req, hours: number, opts: { note?: string; date?: Date; extra?: Partial<Req> } = {}) {
  const h = roundH(hours);
  if (!(h > 0 && h <= SESSION_MAX_HOURS)) throw new Error(`Hours must be more than 0 and at most ${SESSION_MAX_HOURS}.`);
  const ref = doc(collection(db, 'requests', r.id, 'sessions'));
  const data: Record<string, unknown> = {
    hours: h, date: opts.date ? Timestamp.fromDate(opts.date) : serverTimestamp(),
    by: me.name, byUid: me.uid, createdAt: serverTimestamp(),
  };
  if (opts.note?.trim()) data.note = opts.note.trim().slice(0, 500);
  await commitWithTotal(r, h, (b) => b.set(ref, data), opts.extra);
}

export async function editSession(me: Me, r: Req, s: Session, patch: { hours?: number; note?: string; date?: Date }) {
  const h = patch.hours == null ? s.hours : roundH(patch.hours);
  if (!(h > 0 && h <= SESSION_MAX_HOURS)) throw new Error(`Hours must be more than 0 and at most ${SESSION_MAX_HOURS}.`);
  const { deleteField } = await import('firebase/firestore');
  const data: Record<string, unknown> = { hours: h };
  if (patch.note !== undefined) data.note = patch.note.trim() ? patch.note.trim().slice(0, 500) : deleteField();
  if (patch.date) data.date = Timestamp.fromDate(patch.date);
  await commitWithTotal(r, h - s.hours, (b) => b.update(doc(db, 'requests', r.id, 'sessions', s.id), data));
  void me;
}

export async function deleteSession(me: Me, r: Req, s: Session) {
  await commitWithTotal(r, -s.hours, (b) => b.delete(doc(db, 'requests', r.id, 'sessions', s.id)));
  void me;
}

/** Delete a request with its sessions + activity. Children go first so a failure leaves the request findable. */
export async function deleteRequest(r: Req) {
  const subs = await Promise.all(['sessions', 'activity'].map((c) => getDocs(collection(db, 'requests', r.id, c))));
  const refs = subs.flatMap((snap) => snap.docs.map((d) => d.ref));
  for (let i = 0; i < refs.length; i += 400) {
    const b = writeBatch(db);
    refs.slice(i, i + 400).forEach((ref) => b.delete(ref));
    await b.commit();
  }
  await deleteDoc(doc(db, 'requests', r.id));
}

/** Hand-edit the total (null clears it). Noted in the activity log so the history still adds up. */
export async function setActualTotal(me: Me, r: Req, total: number | null) {
  const was = r.loggedHours ?? 0;
  if (total == null) await clearFields(me, r, ['loggedHours']);
  else await updateRequest(me, r, { loggedHours: roundH(total) });
  await addActivity(me, r.id, `Actual hours set to ${total == null ? 0 : roundH(total)}h by hand (was ${was}h)`, 'system');
}

// ---- admin: people, roles, settings ------------------------------------------

export interface Person { uid: string; name: string; email: string; division: DivisionId; role: 'editor' | 'divisionHead' | 'staff' }
export interface Roles { editors: string[]; divisionHeads: Record<string, string>; collections: string[] }

export function usePeople(me: Me | null) {
  const [people, setPeople] = useState<Person[]>([]);
  useEffect(() => {
    if (me?.role !== 'editor') return;
    return onSnapshot(collection(db, 'people'), (snap) =>
      setPeople(snap.docs.map((d) => ({ uid: d.id, ...d.data() }) as Person).sort((a, b) => a.name.localeCompare(b.name))), () => undefined);
  }, [me?.role]);
  return people;
}

export function useRoles(me: Me | null) {
  const [roles, setRoles] = useState<Roles>({ editors: [], divisionHeads: {}, collections: [] });
  useEffect(() => {
    if (me?.role !== 'editor') return;
    return onSnapshot(doc(db, 'config/roles'), (snap) => {
      const d = snap.data() ?? {};
      // The owner counts as editor + Collections user even if the doc lacks him.
      setRoles({ editors: withOwner(d.editors), divisionHeads: d.divisionHeads ?? {}, collections: withOwner(d.collections) });
    }, () => undefined);
  }, [me?.role]);
  return roles;
}

/** A uid list that always contains the owner (config/roles writes must never drop him). */
function withOwner(list: unknown): string[] {
  const l = Array.isArray(list) ? (list as string[]) : [];
  return l.includes(OWNER_UID) ? l : [...l, OWNER_UID];
}

const OWNER_LOCKED = 'Owner - cannot be changed.';

/** Set a person's division + role; keeps config/roles (what the rules read) in step. */
export async function savePerson(_roles: Roles, p: Person, division: DivisionId, role: Person['role']) {
  // Transaction: re-read config/roles so two editors changing different people
  // at once can't overwrite each other's access changes.
  if (p.uid === OWNER_UID) throw new Error(OWNER_LOCKED);
  const { runTransaction } = await import('firebase/firestore');
  await runTransaction(db, async (tx) => {
    const ref = doc(db, 'config/roles');
    const cur = (await tx.get(ref)).data() ?? {};
    const editors = new Set<string>(withOwner(cur.editors));
    const heads: Record<string, string> = { ...(cur.divisionHeads ?? {}) };
    editors.delete(p.uid); delete heads[p.uid];
    if (role === 'editor') editors.add(p.uid);
    if (role === 'divisionHead') heads[p.uid] = division;
    // Keep the Collections list intact (it is a separate per-user toggle); owner always in both lists.
    tx.set(ref, { editors: [...editors], divisionHeads: heads, collections: withOwner(cur.collections), updatedAt: serverTimestamp() });
    tx.set(doc(db, 'people', p.uid), { name: p.name, email: p.email, division, role, updatedAt: serverTimestamp() });
  });
}

/** Turn a person's Collections tab access on or off (config/roles.collections). */
export async function setCollectionsAccess(uid: string, on: boolean) {
  if (uid === OWNER_UID && !on) throw new Error(OWNER_LOCKED);
  const { runTransaction } = await import('firebase/firestore');
  await runTransaction(db, async (tx) => {
    const ref = doc(db, 'config/roles');
    const cur = (await tx.get(ref)).data() ?? {};
    const list = new Set<string>(withOwner(cur.collections));
    if (on) list.add(uid); else list.delete(uid);
    tx.set(ref, {
      editors: withOwner(cur.editors), divisionHeads: cur.divisionHeads ?? {},
      collections: [...list], updatedAt: serverTimestamp(),
    });
  });
}

/** Remove someone from the directory and strip any editor/division-head role. Their requests stay. */
export async function deletePerson(me: Me, p: Person) {
  if (p.uid === OWNER_UID) throw new Error(OWNER_LOCKED);
  if (p.uid === me.uid) throw new Error("You can't delete yourself.");
  const { runTransaction } = await import('firebase/firestore');
  await runTransaction(db, async (tx) => {
    const ref = doc(db, 'config/roles');
    const cur = (await tx.get(ref)).data() ?? {};
    const heads: Record<string, string> = { ...(cur.divisionHeads ?? {}) };
    delete heads[p.uid];
    tx.set(ref, {
      editors: withOwner((cur.editors ?? []).filter((u: string) => u !== p.uid)), divisionHeads: heads,
      collections: withOwner((cur.collections ?? []).filter((u: string) => u !== p.uid)), updatedAt: serverTimestamp(),
    });
    tx.delete(doc(db, 'people', p.uid));
  });
}

export async function saveSettings(s: Settings) {
  const { setDoc } = await import('firebase/firestore');
  await setDoc(doc(db, 'config/settings'), { ...s, updatedAt: serverTimestamp() });
}

// ---- email drafts (spec page 2b) -------------------------------------------

export interface EmailDraft { id: string; subject: string; from: string; fromName: string; preview: string; webLink: string; receivedAt: Timestamp | null; hubTagged?: boolean }

/**
 * needs-attention emails from the last 14 days that Baker tagged with the Outlook category "Hub"
 * (opt-in; untagged mail is hidden, not deleted) and that haven't been accepted or dismissed.
 * The hubTagged filter is client-side: the list is already loaded, and a query on it would need a
 * composite index for no gain.
 */
export function useEmailDrafts(me: Me | null) {
  const [msgs, setMsgs] = useState<EmailDraft[]>([]);
  const [triaged, setTriaged] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (me?.role !== 'editor') return;
    const q = query(collection(db, 'messages'), where('classification', '==', 'needs-attention'), where('removedAt', '==', null));
    const u1 = onSnapshot(q, (snap) => setMsgs(snap.docs.map((d) => ({
      id: d.id, subject: d.get('subject') ?? '', from: d.get('from') ?? '', fromName: d.get('fromName') ?? '',
      preview: d.get('preview') ?? '', webLink: d.get('webLink') ?? '', receivedAt: d.get('receivedAt') ?? null,
      hubTagged: d.get('hubTagged') === true,
    }))), () => setMsgs([]));
    const u2 = onSnapshot(collection(db, 'email_triage'), (snap) => setTriaged(new Set(snap.docs.map((d) => d.id))), () => undefined);
    return () => { u1(); u2(); };
  }, [me?.role]);
  const cutoff = Date.now() - 14 * 86400000;
  return msgs
    .filter((m) => m.hubTagged === true && !triaged.has(m.id) && (m.receivedAt?.toMillis() ?? 0) > cutoff)
    .sort((a, b) => (b.receivedAt?.toMillis() ?? 0) - (a.receivedAt?.toMillis() ?? 0));
}

export async function acceptEmail(messageId: string): Promise<string> {
  const { httpsCallable } = await import('firebase/functions');
  const { functions } = await import('../firebase');
  const r = await httpsCallable(functions, 'acceptEmail')({ messageId });
  return (r.data as { requestId: string }).requestId;
}
export async function dismissEmail(messageId: string) {
  const { httpsCallable } = await import('firebase/functions');
  const { functions } = await import('../firebase');
  await httpsCallable(functions, 'dismissEmail')({ messageId });
}

// ---- Collections (weekly collection lineup; written by the lineup Cloud Functions) ----

export interface LineupRow {
  type: 'reroof' | 'repair';
  deal: string; dealId: string; pm: string | null; jobType?: string; jobStatus?: string;
  collectedInWeek: number; start: string; end: string; firstDay: string;
  contract: number; paid: number; target: number; basis: string; behind: number; multiWeek: boolean;
}
export interface LineupTotals {
  reroof: number; repair: number; all: number; reroofJobs: number; repairJobs: number;
  deposits?: number; depositJobs?: number; earlierSharesOutstanding?: number;
  collectedAllCash?: number; collectedReroof?: number; collectedRepair?: number; collectedAll?: number;
}
export interface Lineup {
  monday: string; sunday: string; rows: LineupRow[]; totals: LineupTotals;
  byPm?: { pm: string; target: number; collected: number; jobs: number }[];
  issues?: string[]; generatedAt?: string; mode?: string; collectedUpdatedAt?: string;
}

/**
 * Does the signed-in user have Collections access? Non-editors cannot read config/roles, so we
 * probe the data the rules protect: a one-doc list query on lineups. The rules decide (a list
 * query is allowed iff hasCollections(), even when the collection is empty); permission-denied
 * = no access. null = still checking.
 */
export function useCollectionsAccess(me: Me | null): boolean | null {
  const [access, setAccess] = useState<boolean | null>(null);
  const uid = me?.uid;
  useEffect(() => {
    if (!uid) { setAccess(null); return; }
    let live = true;
    setAccess(null);
    getDocs(query(collection(db, 'lineups'), limit(1)))
      .then(() => { if (live) setAccess(true); })
      .catch(() => { if (live) setAccess(false); });
    return () => { live = false; };
  }, [uid]);
  return access;
}

/** Users with Collections access: the last 12 weekly lineups, oldest first, live. */
export function useLineups(me: Me | null) {
  const [weeks, setWeeks] = useState<Lineup[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!me) return;
    const q = query(collection(db, 'lineups'), orderBy('monday', 'desc'), limit(12));
    return onSnapshot(q,
      (snap) => { setWeeks(snap.docs.map((d) => d.data() as Lineup).reverse()); setError(null); },
      (e) => setError(e.message));
  }, [me?.uid]);
  return { weeks, error };
}

/** The stored exec PDF for a week (base64), or null when none was saved. */
export async function getLineupPdf(monday: string): Promise<{ pdf: string; name: string } | null> {
  const s = await getDoc(doc(db, 'lineup_pdfs', monday));
  if (!s.exists()) return null;
  const d = s.data();
  return { pdf: String(d.pdf ?? ''), name: String(d.name ?? `Collection-Lineup-${monday}.pdf`) };
}
